import { articleUrls } from "@clockwise/client-commons/src/constants/help-center";
import { paths } from "@clockwise/client-commons/src/constants/site";
import { Link } from "@clockwise/design-system";
import { EcosystemEnum, FocusTimeGoals, Maybe, SmartHoldState } from "@clockwise/schema";
import {
  appPaths,
  webappDefaultWindowName,
} from "@clockwise/web-commons/src/constants/route.constants";
import { useEcosystem } from "@clockwise/web-commons/src/util/ecosystem";
import { getUrlForFeature } from "@clockwise/web-commons/src/util/routes.util";
import {
  getReadableFocusTimeGoals,
  getSmartHoldMovementDescription,
} from "@clockwise/web-commons/src/util/smart-hold.util";
import { useRouter } from "found";
import React from "react";
import { useSelector } from "react-redux";
import { IReduxState } from "../../../state/reducers/root.reducer";
import { ExpandableHelpText } from "./ExpandableHelpText";
import { GoalWithIcon } from "./GoalWithIcon";
import RemoveHold from "./RemoveHold.container";
import SmartHoldStatePanel from "./SmartHoldStatePanel.container";

export type FocusTimeHoldProps = {
  holdId: string;
  orgId: string;
  calendarId: string;
  title: string;
  displayTime: string;
  goals?: Maybe<FocusTimeGoals>;
  state?: Maybe<SmartHoldState>;
};

export const FocusTimeHold = ({
  holdId,
  orgId,
  calendarId,
  title,
  displayTime,
  goals,
  state,
}: FocusTimeHoldProps) => {
  const ecosystem = useEcosystem();
  const isWebExtension = useSelector((state: IReduxState) => state.webExtension.isWebExtension);
  const routerState = useRouter();

  const goalDescription = getReadableFocusTimeGoals({ threshold: goals?.threshold });
  const movementDescription = getSmartHoldMovementDescription({
    holdName: "FocusTimeHold",
    holdState: state?.__typename,
  });

  const onClickEditSettings = () => {
    if (ecosystem === EcosystemEnum.Microsoft) {
      window.open(
        `${paths.webApp}${paths.webSettings}/account/smart-holds/focus-time`,
        "web-settings",
      );
    } else if (isWebExtension) {
      window.open(getUrlForFeature("accountPreferences"), webappDefaultWindowName);
    } else {
      routerState?.router?.replace(appPaths.accountPreferences);
    }
  };

  return (
    <div>
      <div className="cw-heading-xl">{title}</div>
      <div className="cw-body-base cw-mb-4">{displayTime}</div>
      {state && (
        <SmartHoldStatePanel
          holdId={holdId}
          orgId={orgId}
          calendarId={calendarId}
          holdName={"FocusTimeHold"}
          holdState={state.__typename}
        />
      )}
      {!!goalDescription && (
        <GoalWithIcon holdName="FocusTimeHold">
          <div>
            <p className="cw-body-base">
              {goalDescription} · <Link onClick={onClickEditSettings}>Edit settings</Link>
            </p>
          </div>
        </GoalWithIcon>
      )}
      <RemoveHold holdId={holdId} orgId={orgId} />
      <ExpandableHelpText>
        <div className="cw-body-base">
          <p>This Focus Time was automatically created by Clockwise.</p>
          <p className="cw-mt-1 cw-mb-2">{movementDescription}</p>
          <Link href={articleUrls.focusTime} target="_blank">
            Tell me more
          </Link>
        </div>
      </ExpandableHelpText>
    </div>
  );
};
